/**
 * Firestore service for course ratings
 */

import { firestore } from '../../config/firebase';
import { CourseRating } from '@e-learning/shared/src/store/types';
import { collection, doc, getDocs, getDoc, setDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';

const RATINGS_COLLECTION = 'ratings';

/**
 * Get all ratings for a course
 */
export const getCourseRatings = async (courseId: string): Promise<CourseRating[]> => {
  try {
    const ratingsRef = collection(firestore, RATINGS_COLLECTION);
    const q = query(ratingsRef, where('courseId', '==', courseId));
    const querySnapshot = await getDocs(q);
    
    const ratings: CourseRating[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      ratings.push({
        id: doc.id,
        courseId: data.courseId,
        userId: data.userId,
        rating: data.rating,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || new Date().toISOString(),
      });
    });
    
    return ratings;
  } catch (error: any) {
    console.error('Error fetching course ratings:', error);
    throw new Error(error.message || 'Failed to fetch course ratings');
  }
};

/**
 * Get user's rating for a course
 */
export const getUserRating = async (courseId: string, userId: string): Promise<CourseRating | null> => {
  try {
    const ratingsRef = collection(firestore, RATINGS_COLLECTION);
    const q = query(ratingsRef, where('courseId', '==', courseId), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      courseId: data.courseId,
      userId: data.userId,
      rating: data.rating,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || new Date().toISOString(),
    };
  } catch (error: any) {
    console.error('Error fetching user rating:', error);
    throw new Error(error.message || 'Failed to fetch user rating');
  }
};

/**
 * Add or update a rating
 */
export const saveRating = async (rating: Omit<CourseRating, 'id' | 'createdAt' | 'updatedAt'>): Promise<CourseRating> => {
  try {
    // Check if rating already exists
    const existingRating = await getUserRating(rating.courseId, rating.userId);
    
    const now = new Date();
    const ratingData = {
      courseId: rating.courseId,
      userId: rating.userId,
      rating: rating.rating,
      createdAt: existingRating ? Timestamp.fromDate(new Date(existingRating.createdAt)) : Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    
    if (existingRating) {
      // Update existing rating
      const ratingRef = doc(firestore, RATINGS_COLLECTION, existingRating.id);
      await setDoc(ratingRef, ratingData, { merge: true });
      return {
        id: existingRating.id,
        ...rating,
        createdAt: existingRating.createdAt,
        updatedAt: now.toISOString(),
      };
    } else {
      // Create new rating
      const ratingRef = doc(collection(firestore, RATINGS_COLLECTION));
      await setDoc(ratingRef, ratingData);
      return {
        id: ratingRef.id,
        ...rating,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      };
    }
  } catch (error: any) {
    console.error('Error saving rating:', error);
    throw new Error(error.message || 'Failed to save rating');
  }
};

/**
 * Delete a rating
 */
export const deleteRating = async (courseId: string, userId: string): Promise<void> => {
  try {
    const ratingsRef = collection(firestore, RATINGS_COLLECTION);
    const q = query(ratingsRef, where('courseId', '==', courseId), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const ratingDoc = querySnapshot.docs[0];
      await deleteDoc(doc(firestore, RATINGS_COLLECTION, ratingDoc.id));
    }
  } catch (error: any) {
    console.error('Error deleting rating:', error);
    throw new Error(error.message || 'Failed to delete rating');
  }
};

/**
 * Calculate average rating for a course
 */
export const calculateAverageRating = (ratings: CourseRating[]): number => {
  if (ratings.length === 0) return 0;
  const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
  return Math.round((sum / ratings.length) * 10) / 10; // Round to 1 decimal place
};

